import React, { useMemo, useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { WorkoutSession } from '../types';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { EXERCISE_CONFIG } from '../constants';
import { GoogleGenAI } from "@google/genai";
import { useAuth } from '../contexts/AuthContext';
import { subscribeToWorkoutHistory } from '../services/database';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-black border border-white/20 p-3 shadow-2xl">
        <p className="text-white font-mono text-xs uppercase mb-1">{label}</p>
        <p className="text-white font-bold">
          {payload[0].value} REPS
        </p>
      </div>
    );
  }
  return null;
};

export const History: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState<WorkoutSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Subscribe to Real-time Firestore Updates
  useEffect(() => {
    let unsubscribe = () => {};

    if (user) {
      setIsLoading(true);
      unsubscribe = subscribeToWorkoutHistory(user.uid, (data) => {
        setHistory(data);
        setIsLoading(false);
      });
    }

    return () => unsubscribe();
  }, [user]);

  const chartData = useMemo(() => {
    return history.slice(0, 10).reverse().map(s => ({
      name: new Date(s.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' }),
      reps: s.reps,
      type: s.exercise
    }));
  }, [history]);

  const totalReps = history.reduce((acc, curr) => acc + curr.reps, 0);
  const totalTime = Math.floor(history.reduce((acc, curr) => acc + curr.durationSeconds, 0) / 60);

  const handleAIAnalysis = async () => {
    if (history.length === 0) return;

    const apiKey = process.env.API_KEY;
    if (!apiKey) {
      setAnalysis("AI Coach is currently unavailable due to a configuration issue. Please try again later.");
      return;
    }

    setIsAnalyzing(true);
    setAnalysis(null);

    try {
      const ai = new GoogleGenAI({ apiKey });
      
      const historySummary = history.slice(0, 5).map(h => 
        `- ${h.exercise}: ${h.reps} reps, ${h.accuracyScore}% accuracy on ${new Date(h.date).toLocaleDateString()}`
      ).join('\n');

      const prompt = `
        You are an elite fitness coach. Analyze this user's recent workout history:
        ${historySummary}
        
        Provide 3 specific, actionable tips to improve their form or volume. 
        Focus on the accuracy scores. If accuracy is low (<80%), suggest form corrections for that exercise.
        Keep the tone motivating but strict and professional.
      `;

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: prompt,
      });

      setAnalysis(response.text || "Could not generate analysis.");
    } catch (error: any) {
      console.error(error);
      setAnalysis("AI Analysis unavailable at this time. Please try again later.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto py-12 px-4 text-center">
        <div className="inline-block w-12 h-12 border-t-2 border-b-2 border-white rounded-full animate-spin mb-4"></div>
        <p className="text-secondary font-mono text-sm uppercase tracking-widest animate-pulse">Syncing Data Logs...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="mb-10 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tighter">Data Logs</h1>
          <p className="text-secondary font-mono text-xs uppercase tracking-widest flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full animate-pulse bg-green-500`}></span>
            {user ? `Cloud Sync • ${user.email}` : 'Not Connected'}
          </p>
        </div>
        
        {history.length > 0 && (
          <Button 
            onClick={handleAIAnalysis} 
            disabled={isAnalyzing}
            variant="secondary"
            className="text-xs py-3 px-4"
            icon={
              isAnalyzing ? (
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              )
            }
          >
            {isAnalyzing ? 'Analyzing...' : 'Ask AI Coach'}
          </Button>
        )}
      </div>

      {analysis && (
        <Card className="mb-8 border-l-4 border-l-white bg-surface/80">
          <h3 className="text-white font-bold uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="text-xl">⚡</span> AI Performance Report
          </h3>
          <div className="prose prose-invert prose-sm max-w-none">
            <div className="whitespace-pre-wrap font-mono text-sm text-slate-300">
              {analysis}
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-surface border border-white/10 p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-2 opacity-20">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>
          <p className="text-slate-500 font-mono text-xs uppercase tracking-wider mb-2">Total Reps</p>
          <p className="text-4xl font-black text-white">{totalReps}</p>
        </div>
        <div className="bg-surface border border-white/10 p-6 relative overflow-hidden">
           <div className="absolute top-0 right-0 p-2 opacity-20">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <p className="text-slate-500 font-mono text-xs uppercase tracking-wider mb-2">Total Minutes</p>
          <p className="text-4xl font-black text-white">{totalTime}</p>
        </div>
      </div>

      {history.length > 0 ? (
        <>
          <Card title="Volume Analysis" className="mb-8">
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis 
                    dataKey="name" 
                    stroke="#52525b" 
                    fontSize={10} 
                    tickLine={false} 
                    axisLine={false} 
                    tick={{fill: '#71717a', fontFamily: 'monospace'}}
                  />
                  <YAxis 
                    stroke="#52525b" 
                    fontSize={10} 
                    tickLine={false} 
                    axisLine={false} 
                    tick={{fill: '#71717a', fontFamily: 'monospace'}}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(255,255,255,0.05)'}} />
                  <Bar dataKey="reps">
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#FFFFFF" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <h2 className="text-lg font-bold text-white uppercase tracking-wide mb-4">Session Log</h2>
          <div className="space-y-2">
            {history.map((session) => (
              <div key={session.id} className="bg-surface border border-white/5 p-4 flex items-center justify-between hover:border-white/20 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-white text-black flex items-center justify-center text-xs font-bold">
                    {EXERCISE_CONFIG[session.exercise]?.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-bold text-white uppercase text-sm tracking-wide">
                      {EXERCISE_CONFIG[session.exercise]?.name || session.exercise}
                    </h4>
                    <p className="text-xs text-slate-500 font-mono">
                      {new Date(session.date).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-white">{session.reps} <span className="text-[10px] text-slate-500 font-mono uppercase">reps</span></p>
                  <p className="text-[10px] text-slate-500 font-mono uppercase">{session.durationSeconds}s elapsed</p>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12 bg-surface/30 border border-dashed border-white/10">
          <p className="text-slate-500 font-mono text-sm uppercase mb-4">No workout data found.</p>
          <Button onClick={() => navigate('/')} variant="secondary">Initiate Protocol</Button>
        </div>
      )}
    </div>
  );
};
